import { useCallback, useEffect, useRef } from 'react';

export type QueryLoadingDurationAttributes = {
	queryLoadingDurationMs?: number;
};

export type QueryLoadingDurationOptions = {
	enabled: boolean;
	identity: string;
	loading: boolean;
	result: unknown;
	error: unknown;
};

type QueryLoadingState = {
	identity: string;
	loading: boolean;
	startedAt?: number;
	durationMs?: number;
	result?: unknown;
	error?: unknown;
};

// A cached result need not expose a loading interval. Missing timing must stay unknown.
export const useQueryLoadingDuration = ({
	enabled,
	identity,
	loading,
	result,
	error,
}: QueryLoadingDurationOptions): ((
	result: unknown,
	error: unknown,
) => QueryLoadingDurationAttributes) => {
	const state = useRef<QueryLoadingState>({ identity, loading: false });

	useEffect(() => {
		if (!enabled || state.current.identity !== identity) {
			state.current = { identity, loading: false };
		}
		if (!enabled) {
			return;
		}

		const current = state.current;
		if (loading) {
			if (!current.loading) {
				current.startedAt = performance.now();
			}
			current.loading = true;
			current.durationMs = undefined;
			return;
		}

		if (current.loading && current.startedAt !== undefined) {
			const durationMs = performance.now() - current.startedAt;
			current.durationMs = Number.isFinite(durationMs)
				? Math.max(0, Math.round(durationMs))
				: undefined;
		} else if (current.result !== result || current.error !== error) {
			current.durationMs = undefined;
		}
		current.loading = false;
		current.startedAt = undefined;
		current.result = result;
		current.error = error;
	}, [enabled, identity, loading, result, error]);

	return useCallback(
		(observedResult, observedError) => {
			const current = state.current;
			if (
				current.identity !== identity ||
				current.loading ||
				current.result !== observedResult ||
				current.error !== observedError ||
				current.durationMs === undefined
			) {
				return {};
			}
			return { queryLoadingDurationMs: current.durationMs };
		},
		[identity],
	);
};
